/**
 * The solubility rules as general-chemistry courses teach them, for the ions in `ions.ts`.
 *
 * "Soluble" here is the usual classroom threshold of roughly 0.1 mol/L or more dissolving
 * (about 1 g per 100 mL for a typical salt); "slightly soluble" covers the borderline
 * compounds that textbooks list as exceptions; "insoluble" means a precipitate forms at
 * ordinary concentrations. Every "insoluble" salt still dissolves a little — its Ksp says
 * how much. The rules follow OpenStax Chemistry 2e §4.2, the Zumdahl/Brown tables, and the
 * CRC Handbook's solubility data for the borderline cases:
 *
 * 1. Salts of Group 1 cations and NH₄⁺ are soluble (Li₂CO₃, Li₃PO₄, LiF and KClO₄ are only
 *    slightly soluble). Group 1 oxides react with water instead of dissolving.
 * 2. Nitrates, chlorates, perchlorates, permanganates, acetates and hydrogen carbonates are
 *    soluble (silver and mercury(I) acetate are slightly soluble).
 * 3. Chlorides, bromides and iodides are soluble except with Ag⁺, Hg₂²⁺, Cu⁺ and Pb²⁺
 *    (PbCl₂ and PbBr₂ are slightly soluble, in hot water more so).
 * 4. Fluorides are soluble except with Mg²⁺, Ca²⁺, Sr²⁺, Ba²⁺ and Pb²⁺.
 * 5. Sulfates are soluble except with Ba²⁺, Sr²⁺ and Pb²⁺ (Ca²⁺, Ag⁺ and Hg₂²⁺ slightly).
 * 6. Hydroxides are insoluble except with Group 1, NH₄⁺, Ba²⁺ and Sr²⁺ (Ca(OH)₂ slightly).
 * 7. Carbonates, phosphates, sulfites, chromates, oxalates and sulfides are insoluble except
 *    with Group 1 and NH₄⁺ (Group 2 sulfides dissolve by hydrolysis; MgCrO₄ is soluble,
 *    CaCrO₄, SrCrO₄ and MgC₂O₄ slightly).
 * 8. Oxides are insoluble, except those of Group 1 and of Ca, Sr and Ba, which react with
 *    water to give the hydroxide.
 */

import type { Ion, IonId, Salt } from "@/lib/tool-kit/chem-reference/ions";

export type Status = "soluble" | "slightly" | "insoluble" | "reacts";

export type RuleId =
  | "alkali"
  | "solubleAnion"
  | "halide"
  | "fluoride"
  | "sulfate"
  | "hydroxide"
  | "insolubleAnion"
  | "sulfide"
  | "oxide";

export type NoteId =
  | "alkaliException"
  | "hydrolysis"
  | "hydrogenCarbonate"
  | "hot"
  | "none";

export interface Verdict {
  status: Status;
  rule: RuleId;
  /** True when this salt is one of the rule's named exceptions. */
  exception: boolean;
  note: NoteId;
}

const ALKALI: ReadonlySet<IonId> = new Set([
  "Li",
  "Na",
  "K",
  "Rb",
  "Cs",
  "NH4",
]);
const SOLUBLE_ANIONS: ReadonlySet<IonId> = new Set([
  "NO3",
  "ClO3",
  "ClO4",
  "MnO4",
  "CH3COO",
  "HCO3",
  "HSO4",
]);
const INSOLUBLE_ANIONS: ReadonlySet<IonId> = new Set([
  "CO3",
  "PO4",
  "SO3",
  "CrO4",
  "C2O4",
]);
const HALIDES: ReadonlySet<IonId> = new Set(["Cl", "Br", "I"]);
const GROUP_2: ReadonlySet<IonId> = new Set(["Mg", "Ca", "Sr", "Ba"]);

function verdict(
  status: Status,
  rule: RuleId,
  exception = false,
  note: NoteId = "none"
): Verdict {
  return { status, rule, exception, note };
}

/** Slightly soluble Group 1 salts: Li₂CO₃, Li₃PO₄, LiF, KClO₄. */
function alkaliVerdict(cation: IonId, anion: IonId): Verdict {
  if (anion === "O") {
    return verdict("reacts", "oxide");
  }
  const slightLithium = cation === "Li" && ["F", "CO3", "PO4"].includes(anion);
  const slightPerchlorate = cation === "K" && anion === "ClO4";
  if (slightLithium || slightPerchlorate) {
    return verdict("slightly", "alkali", true, "alkaliException");
  }
  return verdict("soluble", "alkali");
}

function halideVerdict(cation: IonId, anion: IonId): Verdict {
  if (["Ag", "Hg2", "Cu1"].includes(cation)) {
    return verdict("insoluble", "halide", true);
  }
  if (cation === "Pb") {
    return anion === "I"
      ? verdict("insoluble", "halide", true)
      : verdict("slightly", "halide", true, "hot");
  }
  return verdict("soluble", "halide");
}

function sulfateVerdict(cation: IonId): Verdict {
  if (["Ba", "Sr", "Pb"].includes(cation)) {
    return verdict("insoluble", "sulfate", true);
  }
  if (["Ca", "Ag", "Hg2"].includes(cation)) {
    return verdict("slightly", "sulfate", true);
  }
  return verdict("soluble", "sulfate");
}

function hydroxideVerdict(cation: IonId): Verdict {
  if (cation === "Ba" || cation === "Sr") {
    return verdict("soluble", "hydroxide", true);
  }
  if (cation === "Ca") {
    return verdict("slightly", "hydroxide", true);
  }
  return verdict("insoluble", "hydroxide");
}

function insolubleAnionVerdict(cation: IonId, anion: IonId): Verdict {
  if (anion === "CrO4") {
    if (cation === "Mg") {
      return verdict("soluble", "insolubleAnion", true);
    }
    if (cation === "Ca" || cation === "Sr") {
      return verdict("slightly", "insolubleAnion", true);
    }
  }
  if (anion === "C2O4" && cation === "Mg") {
    return verdict("slightly", "insolubleAnion", true);
  }
  return verdict("insoluble", "insolubleAnion");
}

function oxideVerdict(cation: IonId): Verdict {
  return ["Ca", "Sr", "Ba"].includes(cation)
    ? verdict("reacts", "oxide", true)
    : verdict("insoluble", "oxide");
}

function solubleAnionVerdict(cation: IonId, anion: IonId): Verdict {
  const note: NoteId = anion === "HCO3" ? "hydrogenCarbonate" : "none";
  if (anion === "CH3COO" && ["Ag", "Hg2"].includes(cation)) {
    return verdict("slightly", "solubleAnion", true);
  }
  return verdict("soluble", "solubleAnion", false, note);
}

/** The solubility verdict for a cation/anion pair. */
export function classify(cation: Ion, anion: Ion): Verdict {
  const c = cation.id;
  const a = anion.id;
  if (ALKALI.has(c)) {
    return alkaliVerdict(c, a);
  }
  if (SOLUBLE_ANIONS.has(a)) {
    return solubleAnionVerdict(c, a);
  }
  if (HALIDES.has(a)) {
    return halideVerdict(c, a);
  }
  if (a === "F") {
    return GROUP_2.has(c) || c === "Pb"
      ? verdict("insoluble", "fluoride", true)
      : verdict("soluble", "fluoride");
  }
  if (a === "SO4") {
    return sulfateVerdict(c);
  }
  if (a === "OH") {
    return hydroxideVerdict(c);
  }
  if (a === "S") {
    return GROUP_2.has(c)
      ? verdict("soluble", "sulfide", true, "hydrolysis")
      : verdict("insoluble", "sulfide");
  }
  if (a === "O") {
    return oxideVerdict(c);
  }
  if (INSOLUBLE_ANIONS.has(a)) {
    return insolubleAnionVerdict(c, a);
  }
  return verdict("insoluble", "insolubleAnion");
}

export const classifySalt = (salt: Salt): Verdict =>
  classify(salt.cation, salt.anion);
